import React, { useState, useEffect } from "react";
import { SquarePen, Trash, ImageUp, Eye, Image as ImageIcon, X, Camera, Plus, MapPin, QrCode, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { deleteCamaraById, getCamaraBillingType } from "../../camaraManager/api/camaraManager.api";
import { getPlainCode, getAllFtpUsers } from "../api/ftpUser.api";
import { getLatestImage } from "../../camera/api/lastImage.api";
import { getStockCams } from "../../stockCams/api/stockCams.api";
import { getObraByFeedID } from "../../obras/api/obras.api";
import { QRCodeCanvas } from "qrcode.react";
import { createInitializationSession } from "../../rop/api/ropApi";
import { isCamaraActiva } from "../../utils/camaraStatus";
import CamStatus from "../../camera/components/camStatus/CamStatus";

const OWNER_LABELS = { Lisual: "Lisual", Tercero: "Terceros" };
// Low-priority columns hidden below lg; the table still scrolls horizontally as a fallback.
const TABLET_HIDDEN_HEADS = ["Chip ICCID", "Camara UID", "Propiedad"];

const CamerasTableCard = ({ camaras, customerId, getColorClass, onEditCamara, onRefresh, onOpenFtpModal, onOpenAddCamaraModal }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [obraModal, setObraModal] = useState(null);
  const [loadingObraFeedID, setLoadingObraFeedID] = useState(null);
  // Device-initialization QR: { camaraUID, session } once generated.
  const [qrModal, setQrModal] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);
  // billingType/renewalDay no existen en el back viejo (nodeBackApi) — viven solo
  // en el webback nuevo, así que se piden acá por identifier y se mergean aparte.
  // Hasta que la respuesta llega para un identifier, ese identifier queda afuera
  // de billingByIdentifier y la fila muestra un estado de carga (no se asume nada).
  const [billingByIdentifier, setBillingByIdentifier] = useState({});
  // owner (propietario) vive en camHardware/stockCams, no en la cámara del nodeBackApi —
  // se linkea por camaraUID === stockCam.serialNumber (ver SaveAndEditCamModal.handleSubmit).
  const [ownerBySerial, setOwnerBySerial] = useState({});
  // status FTP (SQL, ftp_users.status) vive en webback, se linkea por username === camara.feedID
  const [ftpStatusByUsername, setFtpStatusByUsername] = useState({});

  useEffect(() => {
    const identifiers = camaras.map((c) => c.identifier).filter(Boolean);
    if (identifiers.length === 0) return;

    let cancelled = false;
    Promise.allSettled(identifiers.map((identifier) => getCamaraBillingType(identifier))).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((result, idx) => {
        next[identifiers[idx]] = result.status === "fulfilled" ? result.value : null;
      });
      setBillingByIdentifier((prev) => ({ ...prev, ...next }));
    });

    return () => {
      cancelled = true;
    };
  }, [camaras]);

  useEffect(() => {
    let cancelled = false;
    getStockCams()
      .then((stockCams) => {
        if (cancelled) return;
        const next = {};
        (stockCams || []).forEach((stockCam) => {
          if (stockCam.serialNumber) next[stockCam.serialNumber] = stockCam.owner;
        });
        setOwnerBySerial(next);
      })
      .catch((error) => console.error("Error fetching stock cams:", error));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAllFtpUsers()
      .then((ftpUsers) => {
        if (cancelled) return;
        const next = {};
        (ftpUsers || []).forEach((ftpUser) => {
          if (ftpUser.username) next[ftpUser.username] = ftpUser.status;
        });
        setFtpStatusByUsername(next);
      })
      .catch((error) => console.error("Error fetching FTP users:", error));

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleCamaras = showTerminated
    ? camaras
    : camaras.filter(isCamaraActiva);

  const handleGenerateQr = async (camaraUID) => {
    setQrLoading(true);
    try {
      const session = await createInitializationSession(camaraUID);
      setQrModal({ camaraUID, session });
    } catch (error) {
      toast.error("No se pudo generar el código QR");
    } finally {
      setQrLoading(false);
    }
  };

  const handleDelete = async (camaraId) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar esta cámara? Esta acción no se puede deshacer.")) {
      try {
        await deleteCamaraById(camaraId);
        toast.success("Cámara eliminada correctamente");
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error("Error deleting camara:", error);
        toast.error("Error al eliminar la cámara");
      }
    }
  };

  const handleShowFtpPassword = async (camara) => {
    try {
      const customerId = camara.customerId || camara.customer_id;
      
      if (!customerId) {
        toast.error("No se encontró el ID del cliente para esta cámara");
        return;
      }

      const response = await getPlainCode(customerId, camara.identifier);
      const password = response.plainCode || response.password || response;
      
      if (password) {
        window.alert(`La contraseña FTP para ${camara.identifier} es: ${password}`);
      } else {
        toast.info("No se encontró una contraseña configurada para esta cámara");
      }
    } catch (error) {
      console.error("Error fetching FTP password:", error);
      toast.error("Error al obtener la contraseña FTP. Asegúrese de que el usuario FTP exista.");
    }
  };

  const handleShowObra = async (camara) => {
    if (!camara.feedID) {
      toast.error("Esta cámara no tiene un FeedID configurado");
      return;
    }

    setLoadingObraFeedID(camara.feedID);
    try {
      const obra = await getObraByFeedID(camara.feedID);
      setObraModal(obra);
    } catch (error) {
      const message = error?.response?.data?.message || "Error al obtener la obra de esta cámara";
      toast.error(message);
    } finally {
      setLoadingObraFeedID(null);
    }
  };

  const formatDateTime = (filename) => {
    if (!filename) return { date: "Desconocida", time: "Desconocida" };

    // Formato 1: 2025-03-03T130003Z o 2025-03-03T13:00:03Z
    const isoMatch = filename.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):?(\d{2}):?(\d{2})Z/);
    if (isoMatch) {
      const [_, dateStr, hh, mm, ss] = isoMatch;
      const [year, month, day] = dateStr.split('-');
      return { date: `${day}/${month}/${year}`, time: `${hh}:${mm}:${ss}` };
    }

    // Formato 2: SALTUM+Felanix_00_20260506104004.jpg
    // Buscamos específicamente 14 dígitos que suelen estar al final o tras un guión bajo
    const simpleMatch = filename.match(/(\d{8})(\d{6})/);
    if (simpleMatch) {
      const d = simpleMatch[1]; // YYYYMMDD
      const t = simpleMatch[2]; // HHMMSS
      
      return {
        date: `${d.substring(6, 8)}/${d.substring(4, 6)}/${d.substring(0, 4)}`,
        time: `${t.substring(0, 2)}:${t.substring(2, 4)}:${t.substring(4, 6)}`
      };
    }

    return { date: "Desconocida", time: "Desconocida" };
  };

  const handleViewLatestImage = async (feedID) => {
    if (!feedID) {
      toast.error("Esta cámara no tiene un FeedID configurado");
      return;
    }

    try {
      const response = await getLatestImage(feedID);
      // response puede ser un string (viejo formato) o un objeto { url, date } (nuevo formato)
      const imageUrl = typeof response === 'object' ? response?.url : response;
      const apiDateStr = typeof response === 'object' ? response?.date : null;

      if (imageUrl) {
        // Extract filename from the URL
        const url = new URL(imageUrl);
        const pathname = url.pathname;
        const filenameWithExtension = pathname.substring(pathname.lastIndexOf('/') + 1);

        let date, time;

        // Priorizar la fecha de la API si existe, de lo contrario parsear el nombre de archivo
        if (apiDateStr) {
          const apiDate = new Date(apiDateStr);
          if (!isNaN(apiDate.getTime())) {
            date = apiDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
            time = apiDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
          } else {
            const result = formatDateTime(filenameWithExtension);
            date = result.date;
            time = result.time;
          }
        } else {
          const result = formatDateTime(filenameWithExtension);
          date = result.date;
          time = result.time;
        }

        setSelectedImage({
          url: imageUrl,
          date,
          time,
          name: decodeURIComponent(filenameWithExtension) // Store the extracted filename
        });
        setIsModalOpen(true);
      } else {
        toast.info("No se encontró una imagen reciente para esta cámara");
      }
    } catch (error) {
      console.error("Error fetching latest image:", error);
      toast.error("Error al obtener la última imagen");
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 transform hover:scale-[1.01]">
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Información de las Cámaras
          </h4>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenAddCamaraModal?.(customerId)}
              className="min-h-[44px] lg:min-h-0 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Agregar nueva cámara
            </button>
            <button
              onClick={() => setShowTerminated((prev) => !prev)}
              className="min-h-[44px] lg:min-h-0 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {showTerminated ? "Ocultar cámaras terminadas y suspendidas" : "Mostrar cámaras terminadas y suspendidas"}
            </button>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse rounded-xl overflow-hidden shadow-md">
            <thead className="bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800">
              <tr>
                {["Nombre de la cámara", "COD.", "FeedId", "Chip ICCID", "Camara UID", "Propiedad", "Renovación: día de c/mes", "Días Disponibles", "Transmitiendo", "FTP", "Acciones"].map((head) => (
                  <th key={head} className={`${TABLET_HIDDEN_HEADS.includes(head) ? "hidden lg:table-cell " : ""}px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider`}>
                    {head}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleCamaras.map((camara, idx) => {
                const isDisabledFtp = camara.feedID?.startsWith("li") || camara.feedID?.startsWith("fe");
                const billingLoading = !(camara.identifier in billingByIdentifier);
                const billing = billingByIdentifier[camara.identifier];
                const billingType = billing?.billingType;
                const renewalDay = billing?.renewalDay;

                return (
                  <tr
                    key={idx}
                    className="odd:bg-gray-50 even:bg-white hover:bg-gray-100 dark:odd:bg-gray-800 dark:even:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-800 dark:text-gray-100">
                      {camara.Name}
                      {(camara.status === "Terminado" || camara.status === "Suspendida") && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          camara.status === "Suspendida"
                            ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        }`}>
                          {camara.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{camara.identifier}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{camara.feedID}</td>
                    <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{camara.chipICCID || "N/A"}</td>
                    <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{camara.camaraUID || "N/A"}</td>
                    <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {OWNER_LABELS[ownerBySerial[camara.camaraUID]] || "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {billingLoading
                        ? "..."
                        : billingType === "mensual"
                          ? (renewalDay ?? "N/A")
                          : "-"}
                    </td>
                    <td className={`px-6 py-4 text-sm font-semibold ${
                      getColorClass(camara.feedDays?.daysLeft) === 'red' ? 'text-red-600 dark:text-red-400' :
                      getColorClass(camara.feedDays?.daysLeft) === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      {billingLoading ? "..." : billingType === "mensual" ? "-" : camara.feedDays?.daysLeft}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <CamStatus feedID={camara.feedID} />
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <div
                          title={ftpStatusByUsername[camara.feedID] === "active" ? "FTP activo" : ftpStatusByUsername[camara.feedID] === "suspended" ? "FTP suspendido" : "Sin usuario FTP"}
                          className={`w-3 h-3 rounded-full ${
                            ftpStatusByUsername[camara.feedID] === "active" ? "bg-green-500" : "bg-red-500"
                          }`}
                        ></div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-end items-center space-x-2">
                        <button
                          title="Ver Última Imagen"
                          className="p-3 lg:p-2 rounded-full bg-indigo-100 dark:bg-indigo-900 hover:bg-indigo-500 transition-colors group"
                          onClick={() => handleViewLatestImage(camara.feedID)}
                        >
                          <Camera className="w-5 h-5 text-indigo-600 dark:text-indigo-300 group-hover:text-white" />
                        </button>

                        <button
                            title={isDisabledFtp ? "FTP no disponible para este tipo de FeedID" : "Gestionar FTP"}
                            onClick={() => !isDisabledFtp && onOpenFtpModal(camara)}
                            disabled={isDisabledFtp}
                            className={`p-3 lg:p-2 rounded-full transition-colors group ${
                              isDisabledFtp 
                                ? "bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-50" 
                                : "bg-blue-100 dark:bg-blue-900 hover:bg-blue-500"
                            }`}
                        >
                          <ImageUp className={`w-5 h-5 ${
                            isDisabledFtp 
                              ? "text-gray-400 dark:text-gray-500" 
                              : "text-blue-600 dark:text-blue-300 group-hover:text-white"
                          }`} />
                        </button>

                        <button
                          title={isDisabledFtp ? "Ver Clave FTP" : "Clave FTP solo disponible para ciertos FeedID"}
                          onClick={() => isDisabledFtp && handleShowFtpPassword(camara)}
                          disabled={!isDisabledFtp}
                          className={`p-3 lg:p-2 rounded-full transition-colors group ${
                            !isDisabledFtp 
                              ? "bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-50" 
                              : "bg-amber-100 dark:bg-amber-900 hover:bg-amber-500"
                          }`}
                        >
                          <Eye className={`w-5 h-5 ${
                            !isDisabledFtp
                              ? "text-gray-400 dark:text-gray-500" 
                              : "text-amber-600 dark:text-amber-300 group-hover:text-white"
                          }`} />
                        </button>

                        <button
                          title="Ver Obra"
                          onClick={() => handleShowObra(camara)}
                          disabled={loadingObraFeedID === camara.feedID}
                          className="p-3 lg:p-2 rounded-full bg-teal-100 dark:bg-teal-900 hover:bg-teal-500 transition-colors group disabled:opacity-50 disabled:cursor-wait"
                        >
                          <MapPin className="w-5 h-5 text-teal-600 dark:text-teal-300 group-hover:text-white" />
                        </button>

                        <button
                          title="Generar QR"
                          onClick={() => handleGenerateQr(camara.camaraUID)}
                          disabled={!camara.camaraUID || qrLoading}
                          className="p-3 lg:p-2 rounded-full bg-purple-100 dark:bg-purple-900 hover:bg-purple-500 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <QrCode className="w-5 h-5 text-purple-600 dark:text-purple-300 group-hover:text-white" />
                        </button>

                        <button
                          title="Editar Cámara"
                          onClick={() => onEditCamara(camara)}
                          className="p-3 lg:p-2 rounded-full bg-blue-100 dark:bg-blue-900 hover:bg-blue-500 transition-colors group"
                        >
                          <SquarePen className="w-5 h-5 text-blue-600 dark:text-blue-300 group-hover:text-white" />
                        </button>

                        <button 
                          title="Eliminar Cámara"
                          onClick={() => handleDelete(camara.id)}
                          className="p-3 lg:p-2 rounded-full bg-red-100 dark:bg-red-900 hover:bg-red-500 transition-colors group"
                        >
                          <Trash className="w-5 h-5 text-red-600 dark:text-red-300 group-hover:text-white" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl max-h-[90vh] overflow-y-auto max-w-4xl w-full shadow-2xl">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Última captura</h3>
              <div className="flex space-x-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                <p>📅 Fecha: <span className="font-semibold">{selectedImage.date}</span></p>
                <p>⏰ Hora: <span className="font-semibold">{selectedImage.time}</span></p>
              </div>
            </div>

            <div className="relative bg-gray-100 dark:bg-black flex items-center justify-center min-h-[300px] max-h-[70vh]">
              <img
                src={selectedImage.url}
                alt="Latest capture"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 italic truncate">
              Archivo: {selectedImage.name}
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl max-w-sm w-full shadow-2xl p-6 flex flex-col items-center gap-3">
            <button
              onClick={() => setQrModal(null)}
              aria-label="Close"
              className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">QR de configuración de la cámara</h3>
            <div className="bg-white p-3 rounded-lg shadow">
              <QRCodeCanvas value={JSON.stringify(qrModal.session)} size={220} level="M" />
            </div>
            <p className="text-sm font-mono text-gray-700 dark:text-gray-200">{qrModal.camaraUID}</p>
            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              Este código vence en aproximadamente 1 minuto. Regeneralo si deja de funcionar.
            </p>
            <button
              onClick={() => handleGenerateQr(qrModal.camaraUID)}
              disabled={qrLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${qrLoading ? "animate-spin" : ""}`} />
              {qrLoading ? "Generando..." : "Regenerar"}
            </button>
          </div>
        </div>
      )}

      {/* Obra Modal */}
      {obraModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl max-h-[90vh] overflow-y-auto max-w-md w-full shadow-2xl">
            <button
              onClick={() => setObraModal(null)}
              className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600 dark:text-teal-300" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{obraModal.nombre}</h3>
            </div>

            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-800 dark:text-gray-100">Dirección: </span>
                {obraModal.direccion || "Sin dirección cargada"}
              </p>

              {obraModal.ubicacionMaps && (
                <a
                  href={`https://www.google.com/maps?q=${obraModal.ubicacionMaps.lat},${obraModal.ubicacionMaps.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 dark:text-teal-400 hover:underline"
                >
                  <MapPin className="w-4 h-4" />
                  Ver en Google Maps
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CamerasTableCard;
